import { Suspense, lazy } from 'react'
import type * as React from 'react'
import type { JSX } from 'react'
import { AppLayout, LanguagePicker } from '@rs/ui'
// ⚠ CỬA `@rs/policy/routes` — TUYỆT ĐỐI KHÔNG phải `@rs/policy`. Cửa mặc định kéo `authorize.ts` →
//   cedar-wasm + `node:fs`: `node:fs` không tồn tại trong trình duyệt (VỠ lúc dựng `apps/web`) và
//   .wasm nặng phá cổng ngân sách 200 KB gzip. `.dependency-cruiser.cjs` luật
//   `web-khong-dung-cua-cedar-cua-policy` là máy chặn cho đúng chỗ này.
import { HOME_PATH, PUBLIC_ROUTES, canAccessRoute, type CostViewFlags } from '@rs/policy/routes'
import { useTranslation } from 'react-i18next'
import { AuthProvider, useAuth, type Me } from './auth.tsx'
import { ErrorBoundary } from './ErrorBoundary.tsx'
import { navigate, useInternalLinks, useNavKey, useRedirect } from '../shared/navigation.ts'
import { QueryProvider } from '../shared/query-client.tsx'
import { LoginPage } from '../features/login.tsx'
import { VendorLoginPage } from '../features/vendor-login.tsx'
import { VendorChangePasswordPage } from '../features/vendor-change-password.tsx'
import { ForcedPasswordPage, PasswordButton } from '../features/password/PasswordCard.tsx'
// ⚠ KHÔNG nạp trễ: chuông nằm trên HEADER của mọi màn, nên nó ở trong gói khởi tạo dù muốn hay
//   không — bọc `lazy` chỉ thêm một nhịp trắng ngay cạnh logo. Xem khối chú thích ngay dưới.
import { NotifBell } from '../features/NotifBell.tsx'
import { VendorPortalPage } from '../features/vendor-portal/portal.tsx'

/**
 * ══ MÀN NGHIỆP VỤ NẠP TRỄ HẾT — CỨNG HOÁ F32 ═════════════════════════════════════════════════
 *
 * ⭐ GÓI KHỞI TẠO LÀ THỨ MỌI NGƯỜI TẢI, kể cả thầu phụ chỉ vào cổng của họ và khách chỉ tra một
 *   mã đơn. Mỗi màn nghiệp vụ nằm trong đó là vài chục KB mà phần lớn người dùng không bao giờ mở
 *   tới. Sau F31 con số là 198,08/200 KB — tức tính năng sau vỡ ngân sách mà không làm gì sai.
 *
 * ⚠ BỐN MÀN CỐ Ý Ở LẠI trong gói khởi tạo, và lý do KHÔNG phải "quên":
 *   · `LoginPage` · `VendorLoginPage` — màn ĐẦU TIÊN người chưa đăng nhập thấy. Nạp trễ ở đây là
 *     một nhịp trắng trước khi có gì để nhìn, đúng lúc ấn tượng đầu tiên hình thành.
 *   · `HomePage` — đích của mọi lượt đăng nhập và mọi lượt bị đá về.
 *   · `PublicTrackingPage` — render NGOÀI `<Suspense>` (trước khối `page`), nên nạp trễ ở đó cần
 *     một `<Suspense>` riêng; để nguyên cho tới khi có lý do thật.
 *
 * ⚠ `React.lazy` ĐÒI `<Suspense>` bọc ngoài — có ở ngay chỗ dựng `page` bên dưới. Thiếu nó là màn
 *   trắng kèm lỗi runtime, không phải một lần tải chậm.
 */
const lazyPage = <K extends string>(
  load: () => Promise<Record<K, React.ComponentType>>, key: K,
): React.LazyExoticComponent<React.ComponentType> =>
  lazy(() => load().then((m) => ({ default: m[key] })))

/**
 * ⭐ MỌI MÀN NẠP TRỄ, LIỆT KÊ MỘT CHỖ — để ca kiểm gọi được từng cái.
 *
 * ⚠ VÌ SAO CẦN DANH SÁCH NÀY. Gõ sai tên export trong `lazyPage(..., 'TenSai')` KHÔNG hỏng lúc
 *   biên dịch và KHÔNG hỏng lúc chạy test — nó chỉ hỏng khi có người thật mở đúng màn đó, và hiện
 *   ra là MÀN TRẮNG. `router.test.tsx` gọi hết danh sách này và đòi mỗi cái nạp ra một component
 *   thật, nên lỗi gõ sai chết ngay ở CI.
 */
export const LAZY_PAGES: Record<string, () => Promise<{ default: React.ComponentType }>> = {}

const registered = <K extends string>(
  path: string, load: () => Promise<Record<K, React.ComponentType>>, key: K,
): React.LazyExoticComponent<React.ComponentType> => {
  LAZY_PAGES[path] = async () => ({ default: (await load())[key] })
  return lazyPage(load, key)
}

const HealthAccountingPage = registered('/health/ke-toan', () => import('../features/health-accounting.tsx'), 'HealthAccountingPage')
const CatalogPage = registered('/catalog', () => import('../features/catalog.tsx'), 'CatalogPage')
const AzSalesPage = registered('/az/sales', () => import('../features/az-sales.tsx'), 'AzSalesPage')
const QuanTriPage = registered('/quan-tri', () => import('../features/quan-tri.tsx'), 'QuanTriPage')
const SettingsPage = registered('/settings', () => import('../features/settings.tsx'), 'SettingsPage')
const OpsCatalogPage = registered('/az/ops-catalog', () => import('../features/az/ops-catalog.tsx'), 'OpsCatalogPage')
const AzOpsPage = registered('/az/ops', () => import('../features/az-ops.tsx'), 'AzOpsPage')
const PricingPage = registered('/az/pricing', () => import('../features/az/pricing.tsx'), 'PricingPage')
const ArPage = registered('/ar', () => import('../features/ar.tsx'), 'ArPage')
const ApPage = registered('/ap', () => import('../features/ap.tsx'), 'ApPage')
/** §C12c — route CÓ trong sidebar và trong `RESTRICTED_ROUTES` nhưng chưa khai ở đây, nên rơi
 *  xuống `RtStubPage`. Ba route ở tình trạng đó: `/az/billing` (dựng 11/09) · `/az/purchase-docs`
 *  · `/az/vendor-pnl`. Một mục menu bấm vào ra "màn này dựng sau" là một mục menu NÓI DỐI. */
const AzBillingPage = registered('/az/billing', () => import('../features/az-billing.tsx'), 'AzBillingPage')
const AzPurchaseDocsPage = registered('/az/purchase-docs', () => import('../features/az-purchase-docs.tsx'), 'AzPurchaseDocsPage')
const AzVendorPnlPage = registered('/az/vendor-pnl', () => import('../features/az-vendor-pnl.tsx'), 'AzVendorPnlPage')
/**
 * ⚠ MÀN SỔ PHỤ NẠP TRỄ (F22). Nó là màn LỚN NHẤT của phân hệ kế toán và chỉ bốn vai mở tới, trong
 *   khi gói khởi tạo thì MỌI người tải — kể cả thầu phụ chỉ vào cổng của họ. Gộp nó vào gói khởi
 *   tạo đẩy con số lên 198/200 KB, tức tính năng sau vỡ ngân sách mà không làm gì sai.
 * ⚠ `React.lazy` ĐÒI `<Suspense>` bọc ngoài — có ở ngay chỗ dựng `page` bên dưới. Thiếu nó là màn
 *   trắng kèm lỗi runtime, không phải một lần tải chậm.
 */
const FinancePage = registered('/finance', () => import('../features/finance.tsx'), 'FinancePage')
/** Cùng lý do với màn Sổ phụ: màn kế toán, chỉ bốn vai mở tới, nạp trễ để gói khởi tạo nhẹ. */
const AmisDocsPage = registered('/chung-tu', () => import('../features/amis.tsx'), 'AmisDocsPage')
/** Cùng lý do: màn THUẦN GIÁ VỐN, số người mở tới ít hơn hẳn số người tải gói khởi tạo. */
const RailPricePage = registered('/az/gia-duong-sat', () => import('../features/rail.tsx'), 'RailPricePage')
/** Hai màn BÁO CÁO (F26): đọc cùng một cửa, và chỉ mở khi có người thật sự đi xem. */
const StatsPage = registered('/az/stats', () => import('../features/stats.tsx'), 'StatsPage')
const DashboardPage = registered('/az/dashboard', () => import('../features/dashboard.tsx'), 'DashboardPage')
/** Hoa hồng (F27): năm vai mở tới, và bảng khá lớn — nạp trễ. */
const CommissionPage = registered('/az/hoa-hong', () => import('../features/commission.tsx'), 'CommissionPage')
const MyCommissionPage = registered('/hoa-hong-cua-toi', () => import('../features/my-commission.tsx'), 'MyCommissionPage')
/** Chào giá (F28): năm vai điều hành mở tới — nạp trễ như các màn nghiệp vụ khác. */
const BiddingPage = registered('/az/chao-gia', () => import('../features/bidding.tsx'), 'BiddingPage')
/** Vỏ container (F29): mảng độc lập, chỉ người làm bãi mở tới — nạp trễ. */
const ContainerOpsPage = registered('/az/container-ops', () => import('../features/container-ops.tsx'), 'ContainerOpsPage')
/** Kế hoạch & mục tiêu (F31): sáu vai mở tới, bảng khá lớn — nạp trễ. */
const PlanPage = registered('/az/ke-hoach', () => import('../features/plan.tsx'), 'PlanPage')
const ApprovalsPage = registered('/approvals', () => import('../features/approvals.tsx'), 'ApprovalsPage')
/** F19 — tách khỏi tab thứ hai của `/approvals` ngày 19/09/2026 để màn Duyệt chi phẳng đúng bản cũ. */
const DisbursePage = registered('/chi', () => import('../features/disburse.tsx'), 'DisbursePage')
import { PublicTrackingPage } from '../features/public-tracking.tsx'

/**
 * ══ TRANG CÔNG KHAI — MỖI ĐƯỜNG DẪN MỘT MÀN, TẤT CẢ NGOÀI `<AppLayout>` ═════════════════════════
 * Bản cũ `App.tsx` để **cả ba** ngoài `<Layout>` công ty:
 *   · `/login`   (dòng 76) — ngoài cả `<Protected>`, tức route VÔ ĐIỀU KIỆN;
 *   · `/tra-cuu` (dòng 79) — *"Tra cứu đơn hàng CÔNG KHAI cho khách — không đăng nhập"*;
 *   · `/ncc`     (dòng 81) — *"CỔNG THẦU PHỤ — độc lập, KHÔNG dùng Layout công ty (không
 *     sidebar/menu) — user 17/07"*, bọc `<Protected>` nên VẪN cần đăng nhập.
 * Vòng trước mới vá `/tra-cuu`; ba đường còn lại vẫn rơi vào `<AppLayout>` (ĐK3) — một `sale` đã
 * đăng nhập gõ `/ncc` thấy nguyên sidebar "LOGISTICS RT", tức rò cấu trúc menu nội bộ ra cổng
 * ngoài công ty.
 *
 * ⚠ BẢNG NÀY PHỦ `PUBLIC_ROUTES`, KHÔNG PHẢI MỘT DANH SÁCH CHÉP TAY THỨ HAI ═════════════════════
 * Bản trước giữ `CONG_DANG_NHAP = ['/login','/ncc']` — bản sao thứ hai của `PUBLIC_ROUTES`: đổi
 * tên `/login` bên `@rs/policy` thì `laTrangCongKhai('/dang-nhap')` lặng lẽ thành `true` và màn
 * đăng nhập biến thành khung tạm cho khách vãng lai, mà ca test cũ vẫn xanh vì nó cũng gõ tay đúng
 * chuỗi đó. Nay mỗi đường dẫn công khai phải có MỘT màn ở đây, và `router.test.tsx` khẳng định
 * bảng này phủ ĐÚNG `PUBLIC_ROUTES` (không thiếu, không thừa) — đổi tên bên `@rs/policy` là ĐỎ.
 */
const PUBLIC_PAGES: Record<string, (me: Me | null, path: string) => JSX.Element> = {
  // Route VÔ ĐIỀU KIỆN đúng bản cũ: đã đăng nhập vào `/login` vẫn thấy màn đăng nhập. CỐ Ý KHÔNG
  // tự nghĩ ra hành vi "đã đăng nhập thì đá về nhà" — bản cũ không có, thêm là vi phạm §7.
  '/login': () => <LoginPage />,
  // Cổng thầu phụ: chưa đăng nhập → màn đăng nhập riêng (E15, `/ncc/login`); đã đăng nhập → cổng.
  // KHÔNG lọc theo vai — bản cũ cũng không (`<Protected>` chỉ đòi đã đăng nhập).
  // `/ncc/doi-mat-khau` nằm TRONG nhánh `/ncc` nên tự động công khai theo (`PUBLIC_ROUTES`), và
  // đó là ĐÚNG: người mang mật khẩu TẠM chưa đổi thì cũng phải vào được màn đổi (E17).
  '/ncc': (me, path) => {
    if (path === '/ncc/doi-mat-khau') return <VendorChangePasswordPage />
    // F13: cổng thật thay stub. Vai NỘI BỘ vào đây vẫn thấy màn (bản cũ cũng không lọc vai) nhưng
    // MỌI cửa REST của cổng trả 403 cho họ — cổng là chỗ của đối tác, không phải của văn phòng.
    return me ? <VendorPortalPage /> : <VendorLoginPage />
  },
  // Tra cứu công khai — KHÁCH KHÔNG ĐĂNG NHẬP ĐƯỢC, nên đây là nơi bộ chọn ngôn ngữ quan trọng
  // nhất: nút 2 chiều `vi↔en` của bản cũ (`PublicTracking.tsx:65`) khoá VĨNH VIỄN người xem 中文
  // ra ngoài (B6). Màn nghiệp vụ thật dựng ở D27.
  '/tra-cuu': () => <PublicTrackingPage />,
}

/** Màn của đường dẫn công khai này, hoặc `null` nếu nó không công khai. Khớp theo TIỀN TỐ có biên
 *  (`/tra-cuu` nuốt `/tra-cuu/RS-1` nhưng KHÔNG nuốt `/tra-cuu-noi-bo`). */
export const publicPage = (path: string): ((me: Me | null, path: string) => JSX.Element) | null => {
  const matched = PUBLIC_ROUTES.find((x) => path === x || path.startsWith(`${x}/`))
  return matched === undefined ? null : (PUBLIC_PAGES[matched] ?? null)
}

/** Xuất riêng để test khẳng định bảng phủ đúng `PUBLIC_ROUTES`. */
export const PUBLIC_PAGE_PATHS = Object.keys(PUBLIC_PAGES)

/**
 * ══ ĐÍCH CỦA MỌI CÚ ĐÁ ═════════════════════════════════════════════════════════════════════════
 * ⚠ LUẬT: **không bao giờ `replace()` tới một đường dẫn mà chính chốt vừa chạy sẽ từ chối** — làm
 * thế là dựng một vòng lặp tải lại trang, người dùng không thoát ra được bằng bất cứ thao tác nào.
 * Đúng lỗi N1: `ncc` bị đá về `/`, mà `canAccessRoute('ncc','/')` = `false` ⇒ lặp vô hạn.
 *
 * ⭐ Luật này nay được CẤU TRÚC bảo đảm, không chỉ bằng một ca test canh bất biến: chỗ gọi
 * `replace` bên dưới TỰ KIỂM `path === dichKhiBiDa(role)` và dừng lại. Cần cấu trúc vì bất biến
 * kiểu "bảng tra + test" vẫn thủng với vai KHÔNG có trong bảng — VD vai `''`
 * (`app_role.code` là `TEXT PRIMARY KEY` **không có** `CHECK (code <> '')`, tức Postgres nhận
 * được chuỗi rỗng): `dichKhiBiDa('')` = `'/'` mà `canAccessRoute('', '/')` = `false` (fail-closed).
 */
export const redirectTarget = (role: string): string => (role === 'ncc' ? '/ncc' : '/')

/**
 * Đá sang `to` (thay mục lịch sử — `<Navigate replace>` của bản cũ). Trước T2 chỗ này gọi
 * `location.replace(...)` NGAY TRONG lúc vẽ: chạy được chỉ vì nó tải lại cả trang. Nay điều hướng
 * tại chỗ nên phải đi SAU khi vẽ (`useRedirect`), không thì cập nhật kho điều hướng giữa một lượt vẽ.
 */
const Redirect = ({ to }: { to: string }): JSX.Element => {
  useRedirect(to)
  return <p>Đang chuyển…</p>
}

/** Trang gốc: lái sang `HOME_PATH`, hoặc nói thẳng khi vai đó cũng không vào được chỗ ấy. */
const GoHome = ({ canGo, t }: { canGo: boolean; t: (key: 'common.khongCoQuyen') => string }): JSX.Element => {
  if (!canGo) return <p role="alert">{t('common.khongCoQuyen')}</p>
  return <Redirect to={HOME_PATH} />
}

/**
 * Băng "chưa kiểm được phiên" (T35) — `/auth/me` lỗi KHÁC 401. Không vẽ màn đăng nhập (sai sự thật:
 * phiên vẫn còn), không mở ứng dụng (fail-closed): nói thẳng mã lỗi, tự thử lại, có nút thử ngay.
 */
const AuthCheckFailed = ({ status, onRetry }: { status: string; onRetry: () => void }): JSX.Element => {
  const { t } = useTranslation()
  return (
    <main className="min-h-full grid place-items-center bg-slate-50 p-4">
      <div role="alert" className="max-w-md w-full rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 space-y-2">
        <p>{t('common.authCheckFailed', { status })}</p>
        <button type="button" onClick={onRetry}
                className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700">
          {t('common.retryNow')}
        </button>
      </div>
    </main>
  )
}

const Inner = (): JSX.Element => {
  const { t } = useTranslation()
  const { me, loading, checkError, expired, retry, logout } = useAuth()
  /* ⭐ T2 — đọc lại `location.pathname` mỗi lần điều hướng (`pushState` hoặc Back/Forward). Khoá màn
     đổi theo lượt điều hướng: màn dựng lại từ đầu (đọc lại `?open=`…), còn khung + kho cache giữ
     nguyên. Xem `shared/navigation.ts`. */
  const navKey = useNavKey()
  useInternalLinks()
  const path = location.pathname

  // ── ① TRANG CÔNG KHAI — ĐỨNG TRƯỚC MỌI PHÉP XÉT, KHÔNG BỌC `<AppLayout>` ────────────────────
  // `/login` và `/tra-cuu` không cần biết `me`; `/ncc` cần, nên vẫn phải chờ `loading` xong.
  const publicView = publicPage(path)
  if (publicView !== null) {
    if (loading) return <p>{t('common.loading')}</p>
    // `/ncc` cần biết `me`: `/auth/me` lỗi khác 401 thì cũng KHÔNG được coi là chưa đăng nhập.
    if (me === null && checkError !== null && path.startsWith('/ncc')) return <AuthCheckFailed status={checkError} onRetry={retry} />
    return publicView(me, path)
  }

  if (loading) return <p>{t('common.loading')}</p>
  if (!me) {
    if (checkError !== null) return <AuthCheckFailed status={checkError} onRetry={retry} />
    return <LoginPage expired={expired} />
  }

  // ── ② THẦU PHỤ — chép `App.tsx:63` (user 17/07), ĐỨNG TRƯỚC `canAccessRoute` ────────────────
  // `if (user?.role === 'ncc') return <Navigate to="/ncc" replace />`. Nhánh `/ncc` đã được bảng
  // trên xử (ngoài `<AppLayout>`), nên ở đây chỉ còn việc đưa họ về đó.
  if (me.role === 'ncc') return <Redirect to={redirectTarget(me.role)} />

  // ── ②b MẬT KHẨU TẠM (E13 sửa 19/10/2026) — chỉ được đặt mật khẩu mới, không màn nào khác (bản cũ `Login.tsx:309`, không
  //   nút bỏ qua). Máy chủ cũng chặn mọi cửa trừ đổi mật khẩu (`plugins/authorize.ts`) — đây chỉ là lối đi cho người dùng.
  if (me.mustChangePass === true) return <ForcedPasswordPage />

  // ⚠ PHẢI truyền cờ giá vốn TỪNG NGƯỜI (C10, "Nợ mang sang Task 19"). Gọi hai tham số thì một
  //   `sale` được tick `az_see_vendor_cost` bị `location.replace('/')` đá ra khỏi `/az/purchase-docs`
  //   trong khi MÁY CHỦ (`plugins/authorize.ts`, đã truyền cờ) cho vào — client và máy chủ lệch nhau.
  //   `GET /api/v1/auth/me` trả thẳng `req.actor` (`SessionUser`) nên hai trường này luôn có sẵn.
  const flags: CostViewFlags = { azSeeVendorCost: me.azSeeVendorCost, azSalesScope: me.azSalesScope }
  // ── ③ Ẩn menu / đá ra KHÔNG PHẢI là chặn — máy chủ đã chặn ở Task 18; đây chỉ để đỡ chướng mắt.
  if (!canAccessRoute(me.role, path, flags)) {
    const target = redirectTarget(me.role)
    // ⭐ CHỐT CẤU TRÚC (xem `dichKhiBiDa`): đá về nơi mà chính chốt này vừa từ chối = vòng lặp tải
    //    lại trang. Ở đây dừng hẳn và nói thật, thay vì đẩy người dùng vào vòng lặp.
    if (path === target) {
      // Câu chữ ở kho chuỗi (`common.khongCoQuyen`, đủ vi/en/zh) — Task 19 để chuỗi cứng vì
      // i18next chưa dựng; Task 21 nối vào kho.
      return <p role="alert">{t('common.khongCoQuyen')}</p>
    }
    return <Redirect to={target} />
  }

  const page =
    path === '/health/ke-toan' ? <HealthAccountingPage />
    : path === '/catalog' ? <CatalogPage />
    : path === '/az/sales' ? <AzSalesPage />
    : path === '/quan-tri' ? <QuanTriPage />
    : path === '/settings' ? <SettingsPage />
    : path === '/az/ops' ? <AzOpsPage />
    : path === '/az/ops-catalog' ? <OpsCatalogPage />
    : path === '/az/pricing' ? <PricingPage />
    : path === '/ar' ? <ArPage />
    : path === '/ap' ? <ApPage />
    : path === '/az/billing' ? <AzBillingPage />
    : path === '/az/purchase-docs' ? <AzPurchaseDocsPage />
    : path === '/az/vendor-pnl' ? <AzVendorPnlPage />
    : path === '/finance' ? <FinancePage />
    : path === '/az/gia-duong-sat' ? <RailPricePage />
    : path === '/az/hoa-hong' ? <CommissionPage />
    : path === '/hoa-hong-cua-toi' ? <MyCommissionPage />
    : path === '/az/chao-gia' ? <BiddingPage />
    : path === '/az/container-ops' ? <ContainerOpsPage />
    : path === '/az/ke-hoach' ? <PlanPage />
    : path === '/az/stats' ? <StatsPage />
    : path === '/az/dashboard' ? <DashboardPage />
    : path === '/chung-tu' ? <AmisDocsPage />
    : path === '/approvals' ? <ApprovalsPage />
    : path === '/chi' ? <DisbursePage />
    /* ⭐ TRANG GỐC KHÔNG VẼ GÌ — nó LÁI đi, đúng `homePath` của bản cũ. Xem `HOME_PATH` ở
       `@rs/policy/routes` về trang chẩn đoán kỹ thuật mà chỗ này từng vẽ ra cho mọi người dùng.
       ⚠ Vẫn kiểm quyền trước khi lái: vai LẠ không vào được `/az/sales` thì lái sang đó là dựng
         lại đúng vòng lặp mà chốt bên trên vừa chặn. */
    : path === '/' ? <GoHome canGo={canAccessRoute(me.role, HOME_PATH, flags)} t={t} />
    /* ⭐ L15 (rà 16/10) — ĐƯỜNG DẪN LẠ đi như bản cũ `App.tsx:117` (`/az` → `/az/sales`) và `:133`
       (`path="*"` → `<Navigate to="/" replace />`, rồi `/` lái tiếp về trang chủ). Trước đây rơi xuống khung
       `RtStubPage` "Màn này dựng ở ngày sau theo lịch triển khai" — câu nói dối: mọi route đã dựng (23/23). */
    : path === '/az' ? <Redirect to="/az/sales" />
    : <Redirect to="/" />

  return (
    <AppLayout
      me={me}
      currentPath={path}
      languagePicker={<LanguagePicker />}
      notifBell={<NotifBell meName={me.name} onNavigate={navigate} />}
      accountAction={<PasswordButton />}
      onNavigate={navigate}
      onLogout={logout}
    >
      {/* ⭐ T3 — lưới an toàn quanh MÀN, trong khung: lỗi vẽ chỉ thay vùng nội dung, header + menu còn
          nguyên để thoát (bản cũ `Layout.tsx:951`). Khoá theo lượt điều hướng ⇒ bấm sang màn khác là
          khung lỗi tự gỡ, không kẹt lại như một trang trắng thứ hai. */}
      <ErrorBoundary key={navKey} label={path}>
        <Suspense fallback={<p>{t('common.loading')}</p>}>{page}</Suspense>
      </ErrorBoundary>
    </AppLayout>
  )
}

/**
 * ⚠ `QueryProvider` bọc NGOÀI `AuthProvider`: `AuthProvider` gọi `/auth/me`, và ngày nào lời gọi
 *   đó chuyển sang lớp truy vấn thì nó đã có sẵn kho cache. Bọc trong thì phải đổi lại thứ tự,
 *   và đổi thứ tự provider là kiểu thay đổi vỡ ở nơi không ai ngờ.
 */
export const App = (): JSX.Element => (
  <QueryProvider>
    <AuthProvider>
      {/* Tầng ngoài — bản cũ `main.tsx:28` `<ErrorBoundary label="app">`: lỗi ngoài vùng màn (khung,
          màn công khai) vẫn có khung đỏ + nút tải lại, không trắng `#root`. */}
      <ErrorBoundary label="app">
        <Inner />
      </ErrorBoundary>
    </AuthProvider>
  </QueryProvider>
)
